import { PartialFlowStep } from "../steps";
import { getOutgoingStepIds } from "./flow-graph";

/**
 * Checks if connecting sourceStepId to targetStepId would create a cycle in the flow
 * @param steps All steps in the flow
 * @param sourceStepId The step we're connecting from
 * @param targetStepId The step we're connecting to
 * @returns true if connecting would create a cycle, false otherwise
 */
export function wouldCreateCycle(
  steps: PartialFlowStep[],
  sourceStepId: string,
  targetStepId: string,
): boolean {
  // If we're connecting to ourselves, that's a cycle
  if (sourceStepId === targetStepId) {
    return true;
  }

  // Check if there's a path from targetStepId back to sourceStepId
  // If such a path exists, connecting source to target would create a cycle
  return hasPathBetween(steps, targetStepId, sourceStepId);
}

/**
 * Checks if there's a path from startStepId to endStepId using BFS
 * @param steps All steps in the flow
 * @param startStepId Starting step
 * @param endStepId Ending step
 * @returns true if a path exists, false otherwise
 */
function hasPathBetween(
  steps: PartialFlowStep[],
  startStepId: string,
  endStepId: string,
): boolean {
  const visited = new Set<string>();
  const queue: string[] = [startStepId];

  while (queue.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentId = queue.shift()!;

    if (visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);

    // If we reached the end step, a path exists
    if (currentId === endStepId) {
      return true;
    }

    // Find the current step
    const currentStep = steps.find((s) => s.id === currentId);
    if (!currentStep) {
      continue;
    }

    // Add all unvisited next steps to the queue
    for (const nextId of getOutgoingStepIds(currentStep)) {
      if (nextId && !visited.has(nextId)) {
        queue.push(nextId);
      }
    }
  }

  return false;
}
